import * as IoC from 'typescript-ioc';
import { IApiClient, RequestContentType } from '../../infrastructure/api-client';
import {
    EVENT_TRACKER_FILTER_VIEW_TAB,
    IAgentMemoryDTO,
    IAllEventConnectionExportDetails,
    ICreateEventDTO,
    IDeliveryItemDetailsDTO,
    IEditPassportDetailsDTO,
    IEventDetailsDTO,
    IEventRegistrationToken,
    IEventsDAO,
    IEventsListDTO,
    IEventsTrackerListDTO,
    IEventTrackerDetailsDTO,
    IEventTrackerFilterParams,
    IFiltersView,
    IUpdateEventDTO,
    TGetEventsSortKeys,
    TGetEventTrackerSortKeys,
} from '../events.dao';
import { IApiDateConverter } from '../../infrastructure/api-date-converter';
import { RetypePartial } from '../../infrastructure/type-extensions';
import { IEventRequestParams, IPaginationParams, ISortRequestParams, replaceSortRequestParamsWithSortString } from '../shared.dto';
import * as EVENTS_API from '../../constants/api/events-api.constants';
import { FooterLink } from '../../components/_events/_manage-event/_edit-website/website-form-body/website-footer-links/website-footer-link.presentation';
import { EditWebsiteParams } from '../../components/_events/_manage-event/_edit-website/edit-website.viewmodel';
import { Permission } from '../roles.dao';
import { JobDtoType } from '../../domain/jobs';
import { IEventLinkDTO } from '../event-links.dao';

type ICreateEventDTOInternal = RetypePartial<ICreateEventDTO, 'startDateTime' | 'endDateTime' | 'mergedDateTime', string>;
type IEventListDTOInternal = RetypePartial<IEventsListDTO, 'content', IEventDetailsDTOInternal[]>;

type IEventTrackerDTOInternal = RetypePartial<IEventTrackerDetailsDTO, 'startDateTime' | 'endDateTime', string>;
type IEventTrackerListDTOInternal = RetypePartial<IEventsTrackerListDTO, 'content', IEventTrackerDetailsDTOInternal[]>;

interface IEventDetailsDTOInternal extends ICreateEventDTOInternal {
    eventId: number;
    links: IEventLinkDTO[];
    deliveryTeamMembers: IDeliveryItemDetailsDTO[];
    anonymized: boolean;
    anonymizationDate: Date;
    thirdPartyRegistrationEnabled: boolean;
    eventRegistrationToken: string;
}

interface IEventTrackerDetailsDTOInternal extends IEventTrackerDTOInternal {
    eventId: number;
}

class EventsDAO extends IEventsDAO {

    constructor(
        @IoC.Inject private readonly apiClient: IApiClient,
        @IoC.Inject private readonly apiDateConverter: IApiDateConverter,
    ) {
        super();
    }

    public async getEvents(
        params: { accountId: number; deliveryTeamMembers?: string } & IPaginationParams & ISortRequestParams<TGetEventsSortKeys>
    ): Promise<IEventsListDTO> {
        const paramsWithSortString = replaceSortRequestParamsWithSortString(params);
        const response = await this.apiClient.get<IEventListDTOInternal>(EVENTS_API.EVENTS_LIST_URL, paramsWithSortString);
        return {
            ...response.parsedResponse,
            content: response.parsedResponse.content.map(x => this.mapInternalEventDTOToPublicDTO<IEventDetailsDTOInternal>(x))
        };
    }

    public async getAllEvents(
        params: IPaginationParams & ISortRequestParams<TGetEventTrackerSortKeys> & IEventTrackerFilterParams
    ): Promise<IEventsTrackerListDTO> {
        const paramsWithSortString = replaceSortRequestParamsWithSortString(params);
        const response = await this.apiClient.get<IEventTrackerListDTOInternal>(
            EVENTS_API.EVENTS_DASHBOARD_LIST_URL,
            paramsWithSortString
        );

        return {
            ...response.parsedResponse,
            content: response.parsedResponse.content.map(x =>
                this.mapInternalEventDTOToPublicDTO<IEventTrackerDetailsDTOInternal>(x)
            ),
        };
    }

    public async getEventDetails(params: {accountId: number; eventId: number}): Promise<IEventDetailsDTO> {
        const response = await this.apiClient.get<IEventDetailsDTOInternal>(EVENTS_API.EVENTS_DETAILS_URL, params);
        return this.mapInternalEventDTOToPublicDTO<IEventDetailsDTOInternal>(response.parsedResponse);
    }

    public async createEvent(accountId: number, createEventDTO: ICreateEventDTO): Promise<IEventDetailsDTO> {
        const response = await this.apiClient.post<IEventDetailsDTOInternal>(EVENTS_API.EVENTS_DETAILS_URL, {
            ...this.mapPublicEventDTOToInternalDTO(createEventDTO),
            accountId: accountId,
            eventId: '',
        });
        return this.mapInternalEventDTOToPublicDTO<IEventDetailsDTOInternal>(response.parsedResponse);
    }

    public async updateEvent(accountId: number, updateEventDTO: IUpdateEventDTO): Promise<IEventDetailsDTO> {
        const response = await this.apiClient.put<IEventDetailsDTOInternal>(EVENTS_API.EVENTS_DETAILS_URL, {
            ...this.mapPublicEventDTOToInternalDTO(updateEventDTO),
            accountId: accountId,
        });
        return this.mapInternalEventDTOToPublicDTO<IEventDetailsDTOInternal>(response.parsedResponse);
    }

    public async deleteEvent(params: IEventRequestParams) {
        const response = await this.apiClient.delete<JobDtoType>(
            {
                url: EVENTS_API.EVENTS_DETAILS_URL,
                permission: Permission.DELETE_EVENT,
            },
            params
        );
        return response.parsedResponse;
    }

    public async updateEventWebsite(params: IEventRequestParams, editWebsiteParams: EditWebsiteParams): Promise<ICreateEventDTO> {
        const payload = this.preparePayloadForWebsiteEdit(params, editWebsiteParams);

        const response = await this.apiClient.put<File & string & boolean & FooterLink[] & IEventRequestParams[]>(EVENTS_API.EVENTS_SUB_DOMAIN_URL, {
            ...payload
        }, {
            ContentType: RequestContentType.formData
        });
        return response.parsedResponse;
    }

    private mapInternalEventDTOToPublicDTO<T extends {
        startDateTime: string;
        endDateTime: string;
        mergedDateTime;
    }>(internalEventDTO: T) {
        return {
            ...internalEventDTO,
            ...(internalEventDTO.endDateTime && { endDateTime: this.apiDateConverter.apiStringToDate(internalEventDTO.endDateTime) }),
            ...(internalEventDTO.startDateTime && { startDateTime: this.apiDateConverter.apiStringToDate(internalEventDTO.startDateTime) }),
            ...(internalEventDTO.mergedDateTime && { mergedDateTime: this.apiDateConverter.apiStringToDate(internalEventDTO.mergedDateTime) })

        };
    }

    private mapPublicEventDTOToInternalDTO(internalEventDTO: IEventDetailsDTO | ICreateEventDTO) {
        return {
            ...internalEventDTO,
            startDateTime: this.apiDateConverter.dateToAPIString(internalEventDTO.startDateTime),
            ...(internalEventDTO.endDateTime && { endDateTime: this.apiDateConverter.dateToAPIString(internalEventDTO.endDateTime) })
        };
    }

    private preparePayloadForWebsiteEdit(params: IEventRequestParams, editWebsiteParams: EditWebsiteParams) {
        const payload = {
            accountId: params.accountId,
            eventId: params.eventId,
            preventJoinEnabled: editWebsiteParams.preventJoinEnabled,
            customCss: editWebsiteParams.customCss,
            registrationEnabled: editWebsiteParams.registrationEnabled,
        };

        // we adding this key:value pairs if exist to make
        // possible correct validate request on back-end side
        if (editWebsiteParams.subdomain) {
            payload['subdomain'] = editWebsiteParams.subdomain;
        }

        if (editWebsiteParams.footerLinks) {
            for (let i = 0; i < editWebsiteParams.footerLinks.length; i++) {
                payload[`footerLinks[${i}].order`] = editWebsiteParams.footerLinks[i].order;
                payload[`footerLinks[${i}].url`] = editWebsiteParams.footerLinks[i].url;
                payload[`footerLinks[${i}].label`] = editWebsiteParams.footerLinks[i].label;
            }
        }
        if (editWebsiteParams.preventJoinEnabled) {
            payload['preventJoinBefore'] = editWebsiteParams.preventJoinBefore;
        }
        if (editWebsiteParams.registrationEnabled) {
            payload['registrationEnabled'] = editWebsiteParams.registrationEnabled;
        }
        return payload;
    }

    public getEventEditPassportDetails = async (params: IEventRequestParams) => {
        const response = await this.apiClient.get<IEditPassportDetailsDTO>(EVENTS_API.EVENTS_EDIT_PASSPORT, params);
        return response.parsedResponse;
    };

    public updateEventEditPassportDetails = async (params: IEventRequestParams & {subdomain: string; themeId: number}) => {
        const response = await this.apiClient.put<IEditPassportDetailsDTO>(EVENTS_API.EVENTS_EDIT_PASSPORT, params);
        return response.parsedResponse;
    };

    public async getAllEventConnectionExportTemplate( params: { accountId: number; eventId: number }): Promise<IAllEventConnectionExportDetails> {
        const response = await this.apiClient.get<IAllEventConnectionExportDetails>(EVENTS_API.EVENTS_ALL_EVENTS_CONNECTIONS_EXPORT, params);
        return response.parsedResponse;
    }

    public async getEventTrackerFilterViews(params: {filterViewTab: EVENT_TRACKER_FILTER_VIEW_TAB}): Promise<IFiltersView[]> {
        const response = await this.apiClient.get<IFiltersView[]>(EVENTS_API.EVENT_TRACKER_FILTER_VIEW_LIST_URL, params);
        return response.parsedResponse;
    }

    public async createEventTrackerFilterView(view: IFiltersView): Promise<IFiltersView> {
        const response = await this.apiClient.post<IFiltersView>(EVENTS_API.EVENT_TRACKER_FILTER_VIEW, view);
        return response.parsedResponse;
    }

    public async deleteEventTrackerFilterView(params: { userFilterViewId: number }): Promise<void> {
        await this.apiClient.delete(EVENTS_API.EVENT_TRACKER_FILTER_VIEW_DELETE_URL, params);
    }

    public async createEventRegistrationToken(params: IEventRequestParams): Promise<IEventRegistrationToken> {
        const response = await this.apiClient.get<IEventRegistrationToken>(EVENTS_API.CREATE_EVENT_REGISTRATION_TOKEN, params);
        return response.parsedResponse;
    }
    public async updateEventGuidlines(params: IEventRequestParams & {
        eventNotes: string;
    }): Promise<Pick<IEventDetailsDTO, 'eventNotes'>> {
        const response = await this.apiClient.put<Pick<IEventDetailsDTO, 'eventNotes'>>(EVENTS_API.EVENT_NOTES_URL, params);
        return response.parsedResponse;
    }

    public async getAgentMemory(params: IEventRequestParams): Promise<IAgentMemoryDTO> {
        const response = await this.apiClient.get<IAgentMemoryDTO>(EVENTS_API.AGENT_MEMORY_URL, params);
        return response.parsedResponse;
    }

    public async updateAgentMemory(params: IEventRequestParams & {
        agentMemory: string;
    }): Promise<IAgentMemoryDTO> {
        const response = await this.apiClient.put<IAgentMemoryDTO>(EVENTS_API.AGENT_MEMORY_URL, params);
        return response.parsedResponse;
    }
}

export { EventsDAO };
